import { useQuery } from 'react-query';
import api from '../../utils/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, FileText, Calendar, DollarSign, TrendingUp, Activity } from 'lucide-react';

export default function AdminDashboard() {
  const { data, isLoading } = useQuery('admin-stats', () =>
    api.get('/admin/stats').then(r => r.data)
  );

  if (isLoading) return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
    </div>
  );

  const stats = data?.stats || {};

  const statCards = [
    { label: 'إجمالي المستخدمين', value: stats.total_users || 0, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'إجمالي الإعلانات', value: stats.total_listings || 0, icon: FileText, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'إجمالي الحجوزات', value: stats.total_bookings || 0, icon: Calendar, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { label: 'إيرادات المنصة', value: `${Number(stats.total_platform_revenue || 0).toFixed(0)} SAR`, icon: DollarSign, color: 'text-primary-400', bg: 'bg-primary-500/10' },
  ];

  const monthlyData = stats.monthly_bookings || [];
  const categoryData = stats.top_categories || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-white">لوحة التحكم</h1>
        <p className="text-slate-400">نظرة عامة على أداء المنصة</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(s => (
          <div key={s.label} className="glass rounded-2xl p-5">
            <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center mb-3`}>
              <s.icon size={20} className={s.color} />
            </div>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-slate-400 text-sm mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'حجوزات اليوم', value: stats.today_bookings || 0 },
          { label: 'إيرادات هذا الشهر', value: `${Number(stats.month_revenue || 0).toFixed(0)} SAR` },
          { label: 'مستخدمون جدد اليوم', value: stats.today_users || 0 },
        ].map(s => (
          <div key={s.label} className="glass rounded-xl p-4 text-center">
            <p className="text-xl font-bold text-primary-400">{s.value}</p>
            <p className="text-slate-400 text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4">الحجوزات الشهرية</h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                <Line type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-600">
              <Activity size={40} />
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4">أفضل الفئات</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-600">
              <TrendingUp size={40} />
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      {stats.recent_bookings && stats.recent_bookings.length > 0 && (
        <div className="glass rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4">آخر الحجوزات</h3>
          <div className="space-y-2">
            {stats.recent_bookings.map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 bg-slate-800/40 rounded-xl text-sm">
                <span className="text-white font-mono">{b.booking_reference}</span>
                <span className="text-slate-400">{b.listing_title}</span>
                <span className="text-primary-400">{b.total_amount} {b.currency}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>{b.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
